import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useClickStore } from '../store/clickStore.js';
import { useShallow } from 'zustand/react/shallow';

const _matrix = new THREE.Matrix4();
const _position = new THREE.Vector3();
const _quaternion = new THREE.Quaternion();
const _scale = new THREE.Vector3();

const GOLD = '#FFD700';
const RADIUS_PAD = 1.22;
const BLOCK_PAD = 1.05;

function pulse(material) {
  if (!material) return;
  const mats = Array.isArray(material) ? material : [material];
  const opacity = 0.5 + Math.sin(Date.now() * 0.004) * 0.35;
  for (let i = 0; i < mats.length; i += 1) {
    if (mats[i]) mats[i].opacity = opacity;
  }
}

function isHoleTrace(object) {
  if (!object) return false;
  if (object.isLine || object.isLine2 || object.isLineSegments) return true;
  const name = String(object.name || '');
  return name === 'Holes' || name === 'HolesHighlight';
}

function selectionKind(object, instanceId) {
  if (!object) return null;
  if (isHoleTrace(object)) return null;
  const hasId = Number.isInteger(instanceId) && instanceId >= 0;
  if (object.isInstancedMesh && hasId) return 'instance';
  if (object.isPoints && hasId) return 'block';
  if (object.isMesh && object.geometry && !object.isInstancedMesh) return 'mesh';
  return null;
}

function applyTransform(mesh, obj, kind, instanceId, details) {
  if (!mesh || !obj) return;

  if (kind === 'instance') {
    if (instanceId >= (obj.count ?? 0)) return;
    obj.getMatrixAt(instanceId, _matrix);
    _matrix.premultiply(obj.matrixWorld);
    _matrix.decompose(_position, _quaternion, _scale);
    const sphere = obj.geometry?.type?.includes('Sphere');
    if (sphere) {
      _scale.multiplyScalar(RADIUS_PAD);
    } else {
      _scale.x *= RADIUS_PAD;
      _scale.z *= RADIUS_PAD;
    }
    mesh.position.copy(_position);
    mesh.quaternion.copy(_quaternion);
    mesh.scale.copy(_scale);
    return;
  }

  if (kind === 'block') {
    const posAttr = obj.geometry?.attributes?.position;
    if (!posAttr || instanceId >= posAttr.count) return;
    _position.fromBufferAttribute(posAttr, instanceId);
    obj.localToWorld(_position);
    const sizeAttr = obj.geometry.attributes.aSize;
    const fallback = Math.max(sizeAttr ? sizeAttr.getX(instanceId) : 2.5, 0.35);
    // Packed sizes are East, North, RL; scene axes are X=East, Y=RL, Z=North.
    const sx = Number(details?.SX);
    const sy = Number(details?.SY);
    const sz = Number(details?.SZ);
    mesh.position.copy(_position);
    mesh.quaternion.identity();
    mesh.scale.set(
      (Number.isFinite(sx) && sx > 0 ? sx : fallback) * BLOCK_PAD,
      (Number.isFinite(sz) && sz > 0 ? sz : fallback) * BLOCK_PAD,
      (Number.isFinite(sy) && sy > 0 ? sy : fallback) * BLOCK_PAD
    );
    return;
  }

  obj.updateWorldMatrix(true, false);
  obj.matrixWorld.decompose(_position, _quaternion, _scale);
  mesh.position.copy(_position);
  mesh.quaternion.copy(_quaternion);
  mesh.scale.copy(_scale);
}

const highlightMat = {
  color: GOLD,
  transparent: true,
  opacity: 0.7,
  depthTest: false,
  toneMapped: false,
};

export default function HighlightedObject() {
  const meshRef = useRef();
  const invalidate = useThree((s) => s.invalidate);
  const { objectDetailsObject, objectDetails, clickType, instanceId } = useClickStore(
    useShallow((state) => ({
      objectDetailsObject: state.objectDetailsObject,
      objectDetails: state.objectDetails,
      clickType: state.clickType,
      instanceId: state.instanceId,
    }))
  );

  const kind = clickType === 'info' ? selectionKind(objectDetailsObject, instanceId) : null;

  const clonedGeom = useMemo(() => {
    if ((kind !== 'instance' && kind !== 'mesh') || !objectDetailsObject?.geometry) return null;
    return objectDetailsObject.geometry.clone();
  }, [kind, objectDetailsObject]);

  useLayoutEffect(() => {
    return () => {
      clonedGeom?.dispose?.();
    };
  }, [clonedGeom]);

  useLayoutEffect(() => {
    applyTransform(meshRef.current, objectDetailsObject, kind, instanceId, objectDetails);
    if (kind) invalidate();
  }, [kind, objectDetailsObject, instanceId, objectDetails, clonedGeom, invalidate]);

  useFrame(() => {
    const mesh = meshRef.current;
    if (!mesh || !kind) return;
    pulse(mesh.material);
    applyTransform(mesh, objectDetailsObject, kind, instanceId, objectDetails);
    invalidate();
  });

  if (!kind) return null;

  const skipPick = { mdvSkipPick: true };

  if (kind === 'block') {
    return (
      <mesh ref={meshRef} userData={skipPick} raycast={() => false} frustumCulled={false}>
        <boxGeometry args={[1, 1, 1]} />
        <meshBasicMaterial {...highlightMat} />
      </mesh>
    );
  }

  if (!clonedGeom) return null;

  return (
    <mesh ref={meshRef} userData={skipPick} raycast={() => false} frustumCulled={false}>
      <primitive object={clonedGeom} attach="geometry" />
      <meshBasicMaterial {...highlightMat} side={THREE.DoubleSide} />
    </mesh>
  );
}
